import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Modal, ModalDialog, ModalOverflow, Stack, Tooltip, Typography } from "@mui/joy";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FieldEditTable } from "./FieldEditTable";
import toast from "react-hot-toast";
import { authenticatedApi } from "api";
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateOpportunity, useUpdateOpportunity } from "@api/opportunityService";
import { ensureDateObject, formatDateForDatabase } from "@utils/dates";
import { addDays, differenceInDays } from "date-fns";

const fields = [
    /*
    {
        name: 'oppinfo',
        label: false,
        type: 'info',
        content: 'Opportunity Information'
    },
    */
    {
        name: 'opportunity_id',
        label: 'Opportunity',
        type: 'relationship',
        endpoint: 'opportunities',
        conditional: object => !object?.has_opportunity,
        showRequired: true,
        config: {
            required: true,
        }
    },

    {
        name: 'name',
        type: 'text',
        placeholder: 'e.g. 2024 Clean Up Day',
        showRequired: true,
        config: {
            required: true,
        }
    },
    {
        name: 'start_date',
        label: 'Start Date',
        type: 'date',
        placeholder: 'e.g. 05/23/2016',
        showRequired: true,
        config: {
            required: true,
        },
        content: <Typography>This needs to be the <b>first</b> date for shifts.</Typography>,
    },

    {
        name: 'workplan_id',
        label: 'Work Plan',
        type: 'relationship',
        endpoint: 'work-plans',
    },
    {
        name: 'event_id',
        label: 'Event',
        type: 'relationship',
        endpoint: 'events',
    },
];

/*
const cloneOptionFields = [
    {
        name: 'cloneinfo',
        label: false,
        type: 'info',
        content: 'Cloning Options'
    },
    {
        name: 'contacts',
        label: false,
        type: 'boolean',
        inputLabel: 'Copy attached contacts',
    },
    {
        name: 'involvements',
        label: false,
        type: 'boolean',
        inputLabel: 'Copy attached involvements',
    },
    {
        name: 'tasks',
        label: false,
        type: 'boolean',
        inputLabel: 'Copy attached tasks',
    },
    {
        name: 'copy_budget',
        label: false,
        type: 'select',
        placeholder: 'Choose budget type...',
        conditional: object => !!object?.tasks,
        options: [
            {label: 'Copy old actual budget to new estimated budget', value: 'actual_to_estimate'},
            {label: 'Keep estimated budget only', value: 'keep_budget'},
            {label: 'Don\'t copy budget', value: 'clear_budget'},
        ],
    },
    {
        name: 'tasks_assignments',
        label: false,
        type: 'boolean',
        inputLabel: 'Copy task assignments',
        conditional: object => !!object?.tasks,
    },
    {
        name: 'tasks_dates',
        label: false,
        type: 'boolean',
        inputLabel: 'Backdate tasks from work plan date',
        conditional: object => !!object?.tasks,
    },
];
*/

export function OpportunityCloneModal({opportunity = null, open, setOpen}) {
    const navigate = useNavigate();

    const [saving, setSaving] = useState(false);

    const createMutation = useCreateOpportunity();

    let defaultValues = {
        name: '',
        /*
        workplan_date: null,

        copy_budget: 'actual_to_estimate',
        contacts: true,
        involvements: true,
        tasks: true,
        tasks_assignments: true,
        tasks_dates: true,
        */
    };

    if(opportunity) {
        defaultValues.has_opportunity = true;
        defaultValues.opportunity_id = opportunity.id;
    }

    const formMethods = useForm({
        defaultValues,
    });
    const {
        setValue,
        reset,
        handleSubmit,
        watch,
        getValues,
        formState: { isSubmitting, errors, isValid, isDirty }
    } = formMethods;

    // without this, the opportunity wont get identified correctly
    useEffect(() => {
        reset(defaultValues);
    }, [opportunity]);

    const onSubmit = (fields) => {
        asyncSubmit(fields);
    };

    const asyncSubmit = async (fields) => {
        if(saving) {
            return;
        }

        setSaving(true);

        /*
        |--------------------------------------------------------------------------
        | Cloning process explained
        |--------------------------------------------------------------------------
        |
        | 1. We create the new opportunity WITHOUT jobs
        | 2. We build a mapping of old to new time slots
        | 3. We update the job time slots to match NEW IDs
        |
        */
        toast.loading('Cloning...', {
            id: 'clone-opportunity',
        });

        // 0. get previous opportunity
        const previousOpportunity = await authenticatedApi.get(`/opportunities/${fields.opportunity_id}?include=all`);

        // 1. create new
        let newOpportunity = {
            clone: true,
            name: fields.name,
            description: previousOpportunity.data.description,
            workplan_id: fields.workplan_id ?? null,
            event_id: fields.event_id ?? null,
        };

        let newOpportunityTimeSlots = [];

        // get oldest time slot so we can use it to diff
        let orderedSlots = previousOpportunity.data.time_slots.sort((a, b) => {
            return ensureDateObject(a.date) > ensureDateObject(b.date);
        });
        let oldestSlotDate = ensureDateObject(orderedSlots[0].date);
        let newStartDate = ensureDateObject(fields.start_date);
        let diff = differenceInDays(newStartDate, oldestSlotDate);

        for(const timeSlot of previousOpportunity.data.time_slots) {
            let oldSlotDate = ensureDateObject(timeSlot.date);
            let newSlotDate = addDays(oldSlotDate, diff);

            newOpportunityTimeSlots.push({
                date: formatDateForDatabase(newSlotDate),
                start_time: timeSlot.start_time,
                end_time: timeSlot.end_time,
                location: timeSlot.location,
            });
        };

        newOpportunity.time_slots = newOpportunityTimeSlots;

        // todo: if error, display error toast
        const performCreateMutation = await createMutation.mutateAsync({
            ...newOpportunity,
        });

        // 2. build time slot mapping
        let timeSlotMapping = {};
        for(const timeSlotIndex in previousOpportunity.data.time_slots) {
            const oldSlot = previousOpportunity.data.time_slots[timeSlotIndex];
            const newSlot = performCreateMutation.time_slots[timeSlotIndex];
            timeSlotMapping[oldSlot.id] = newSlot.id;
        }

        // 3. build out jobs
        let newOpportunityJobs = [];
        for(const job of previousOpportunity.data.jobs) {
            let newJob = {
                name: job.name,
                description: job.description,
                quantity: job.quantity,
            };

            let availableTimeSlots = [];
            for(const jobSlot of job.available_time_slots) {
                // update job available_time_slots format to match new layout
                let oldJobSlotId = typeof jobSlot === 'number' ? jobSlot : jobSlot.id;

                availableTimeSlots.push(timeSlotMapping[oldJobSlotId]);
            }
            newJob.available_time_slots = availableTimeSlots;

            newOpportunityJobs.push(newJob);
        }

        const updateOpportunity = authenticatedApi.put(`/opportunities/${performCreateMutation.id}`, {
            jobs: newOpportunityJobs,
        });

        updateOpportunity.then(response => {
            toast.success('The opportunity was cloned!', {
                id: 'clone-opportunity',
            });

            setOpen(false);
            navigate(`/opportunities/${performCreateMutation.id}`);
        }).finally(() => {
            setSaving(false);
        });

        /*
        toast.promise(performCreateMutation, {
            loading: `Cloning...`,
            success: `The opportunity was cloned!`,
            error: (err) => `There was an error while cloning.`,
        }, {id: 'clone-opportunity'});
        */
    }

    return (
    <Modal open={open} onClose={() => saving ? null : setOpen(false)}>
        <ModalOverflow>
        <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
            <ModalDialog sx={{width: '100%', maxWidth: '800px'}}>
                <DialogTitle>
                    {!!opportunity && `Clone ${opportunity.name}`}
                    {!opportunity && `Clone Opportunity`}
                    {/* {task ? <>Add Time to <em>{task.task_name}</em></> : 'Add Time'} */}
                </DialogTitle>
                <Divider/>
                <Box sx={{
                    marginInline: 'var(--Divider-inset)',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2
                }}
                >
                    {/* <Box sx={{flex: '1 1 50%'}}> */}
                    <FieldEditTable mode="row" fields={fields}/>
                    {/*
                    </Box>
                    <Box sx={{flex: '1 1 50%', /*borderLeft: 1, borderLeftColor: 'var(--joy-palette-divider)'*\/}}>
                    <FieldEditTable mode="row" fields={cloneOptionFields}/>
                    </Box>
                */}
                </Box>
                <DialogActions>
                    <Button
                        disabled={!isValid}
                        loading={saving}
                        type="button" // this can't be submit type or it will submit the parent form
                        onClick={() => {
                        onSubmit(getValues());
                        }}
                    >
                        Clone
                    </Button>
                    <Button
                        disabled={saving}
                        variant="plain"
                        color="neutral"
                        onClick={() => {
                            setOpen(false);
                            try {
                                // onCancel();
                            } catch(e) {
                                //
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </ModalDialog>
        </FormProvider>
        </form>
        </ModalOverflow>
    </Modal>
    )
}
